import { useCallback, useEffect, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import { createPortal } from 'react-dom';

import { CheckCircle2, ClipboardCheck, CreditCard, PackageCheck, RefreshCw, ShieldCheck, X, XCircle } from 'lucide-react';

import axiosClient from '../../api/axiosClient';

import { useConfirm } from '../../components/feedback/ConfirmProvider';

import { useAlerts } from '../../components/feedback/AlertProvider';
import { useLanguage } from '../../contexts/LanguageContext';

import '../../styles/Marketing/field-sales-approval.css';



const money = (value) => Number(value || 0).toLocaleString('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' DH';

const date = (value) => value ? new Date(value).toLocaleDateString('en-GB') : '—';

const tabs = [

  { path: '/admin/sales-verification', text: 'Overview', icon: ShieldCheck },

  { path: '/admin/sales-verification/subscriptions', text: 'Subscriptions', icon: CreditCard },

  { path: '/admin/sales-verification/orders', text: 'QR print orders', icon: PackageCheck },

];



export default function FieldSalesApprovalCenter({ view = 'overview' }) {

  const { t } = useLanguage();
  const tr = useCallback((key, fallback) => {
    const path = `fieldSalesApproval.${key}`;
    const translated = t(path);
    return translated && translated !== path ? translated : fallback;
  }, [t]);
  const { confirm } = useConfirm();

  const { showAlert } = useAlerts();

  const [report, setReport] = useState(null);

  const [loading, setLoading] = useState(true);

  const [busy, setBusy] = useState(null);

  const [notes, setNotes] = useState({});

  const [managerEmails, setManagerEmails] = useState({});

  const [externalMenuUrls, setExternalMenuUrls] = useState({});

  const [openId, setOpenId] = useState(null);

  const selected = (report?.registrations || []).find(item => item.id === openId) || null;

  useEffect(() => {

    if (!selected) return undefined;

    const onKeyDown = (event) => {

      if (event.key === 'Escape') setOpenId(null);

    };

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    document.addEventListener('keydown', onKeyDown);

    return () => {

      document.body.style.overflow = previousOverflow;

      document.removeEventListener('keydown', onKeyDown);

    };

  }, [selected]);

  const load = useCallback(async () => {

    setLoading(true);

    try {

      const response = await axiosClient.get('/admin/sales-verification');

      setReport(response.data?.data || null);

    } catch (e) {

      showAlert({ type: 'error', title: tr('t7ffda526c2', "Could not load approvals"), message: e?.response?.data?.message || 'Could not load approvals.' });

    } finally { setLoading(false); }

  }, [showAlert]);

  useEffect(() => { load(); }, [load]);



  const entries = report?.registrations || [];

  const summary = report?.summary || {};

  const visible = useMemo(() => entries.filter(r => view === 'subscriptions'

    ? r.subscription_period !== 'none'

    : view === 'orders' ? r.lines?.length > 0 : true), [entries, view]);

  const change = async (item, kind, decision) => {

    const isActivation = kind === 'subscription' && decision === 'approve';

    const managerEmail = (managerEmails[item.id] || '').trim();

    const creatingOrder = kind === 'qr-order' && decision === 'approve';

    const needsQrManager = creatingOrder && !item.manager_user_id && !item.establishment_id;

    const requiresEmail = isActivation || needsQrManager;

    const externalMenuUrl = (externalMenuUrls[item.id] ?? item.external_menu_url ?? '').trim();

    if (creatingOrder && externalMenuUrl && !/^https?:\/\/[^\s/]+/i.test(externalMenuUrl)) {

      showAlert({ type: 'error', title: tr('te1acb0fe84', "Invalid menu URL"), message: 'Enter an http\:// or https\:// menu URL.' });

      return;

    }

    if (requiresEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(managerEmail)) {

      showAlert({ type: 'error', title: tr('tcfb1a1dee1', "Manager email required"), message: 'Enter a valid manager email before creating the customer account or activating a subscription.' });

      return;

    }

    const label = decision.replaceAll('_', ' ');

    const confirmed = await confirm({

      title: decision === 'reject' ? tr('te18e81b733', "Reject request") : 'Confirm ' + label,

      message: isActivation

        ? `Create a restaurant manager account for ${managerEmail} and activate the ${item.subscription_period} subscription for ${item.business_name}?`

        : creatingOrder

          ? `${needsQrManager ? `Create a customer manager account for ${managerEmail} and ` : ''}create a pending QR print order for ${item.business_name}${externalMenuUrl ? ` using external menu ${externalMenuUrl}` : ''}? No subscription will be activated.`

          : `Are you sure you want to ${label} for ${item.business_name}?`,

      itemName: item.business_name || `Registration #${item.id}`,

      confirmText: decision === 'reject' ? tr('td98ac12774', "Reject") : tr('t70d9be9b13', "Confirm"),

      cancelText: tr('tea4788705e', "Cancel"),

      closeText: tr('td3d2e61733', "Close"),

      variant: decision === 'reject' ? 'danger' : 'default',

    });

    if (!confirmed) return;

    setBusy(`${kind}:${item.id}`);

    try {

      await axiosClient.patch(`/admin/sales-verification/registrations/${item.id}/${kind}`,

        { decision, note: notes[`${kind}:${item.id}`] || null, ...(requiresEmail ? { manager_email: managerEmail } : {}), ...(creatingOrder ? { external_menu_url: externalMenuUrl || null } : {}) });

      showAlert({ type: 'success', title: tr('tdf7cba070b', "Action completed"), message: `${item.business_name}: ${label} saved.` });

      await load();

    } catch (e) {

      showAlert({ type: 'error', title: tr('ta89f7e3896', "Action failed"), message: e?.response?.data?.errors?.manager_email?.[0] || e?.response?.data?.errors?.managerEmail?.[0] || e?.response?.data?.message || tr('t5953155d74', "Could not save this decision.") });

    } finally { setBusy(null); }

  };

  const buttons = (item, kind) => {

    const isSubscription = kind === 'subscription';

    const status = isSubscription ? item.subscription_status : item.qr_order_status;

    const payment = isSubscription ? item.payment_status : item.qr_payment_status;

    const choices = isSubscription

      ? [...(payment !== 'paid' ? [['verify_payment', tr('t1c50c0165c', "Verify payment")]] : []),

          ...(status === 'pending' && payment === 'paid' ? [['approve', tr('t25ed3ee01b', "Activate subscription")]] : []),

          ...(status === 'pending' ? [['reject', tr('td98ac12774', "Reject")]] : []),

          ...(status === 'rejected' ? [['reset', tr('tb8244c0945', "Reopen")]] : [])]

      : [...(!item.qr_print_order_id && status === 'pending' ? [['approve', tr('t3e5afbc601', "Create pending customer order")], ['reject', tr('td98ac12774', "Reject")]] : []),

          ...(!item.qr_print_order_id && status === 'rejected' ? [['reset', tr('tb8244c0945', "Reopen")]] : [])];

    return <div className="fsa-controls">

      <label className="fsa-note-label">{tr('ta4d05d1e96', "Review note")}

        <input value={notes[`${kind}:${item.id}`] || ''} onChange={e => setNotes(current => ({ ...current, [`${kind}:${item.id}`]: e.target.value }))} placeholder={tr('te20c3ee763', "Optional review note")} maxLength={2000}/>

      </label>

      {isSubscription && status === 'pending' && payment === 'paid' && <label className="fsa-note-label">{tr('t83cd22d632', "Restaurant manager email (required to activate)")}

        <input type="email" autoComplete="off" value={managerEmails[item.id] || ''} onChange={e => setManagerEmails(current => ({ ...current, [item.id]: e.target.value }))} placeholder="manager@example.com" maxLength={255} />

      </label>}

      {!isSubscription && !item.qr_print_order_id && status === 'pending' && !item.manager_user_id && !item.establishment_id && <label className="fsa-note-label">{tr('t57d2174865', "Customer manager email (required for account creation, no subscription needed)")}

        <input type="email" autoComplete="off" value={managerEmails[item.id] || ''} onChange={e => setManagerEmails(current => ({ ...current, [item.id]: e.target.value }))} placeholder="manager@example.com" maxLength={255} />

      </label>}

      {!isSubscription && !item.qr_print_order_id && status === 'pending' && <label className="fsa-note-label">{tr('tb561ad9489', "External menu URL (optional)")}

        <input type="url" inputMode="url" autoComplete="url" value={externalMenuUrls[item.id] ?? item.external_menu_url ?? ''} onChange={e => setExternalMenuUrls(current => ({ ...current, [item.id]: e.target.value }))} placeholder="https\://restaurant.example/menu" maxLength={2048} />

      </label>}

      <div className="fsa-buttons">{choices.map(([decision, text]) => <button

        type="button" key={decision} disabled={busy !== null}

        className={`fsa-action ${decision === 'reject' ? 'is-danger' : ''}`}

        onClick={() => change(item, kind, decision)}>

        {decision === 'reject' ? <XCircle size={15}/> : <CheckCircle2 size={15}/>} {text}

      </button>)}</div>

    </div>;

  };

  return <main className="fsa-page">

    <div className="fsa-heading"><div><span className="fsa-kicker"><ShieldCheck size={16}/> {tr('t567879a0d4', "LMENU SUPER ADMIN")}</span>

      <h1>{tr('td391ef228a', "Sales verification")}</h1><p>{tr('tf116746ad9', "Review registrations, verify payments, and approve subscriptions and QR print orders separately.")}</p></div>

      <button type="button" className="fsa-refresh" disabled={loading} onClick={load}><RefreshCw size={16}/> {tr('t63a6a88c06', "Refresh")}</button>

    </div>

    <nav className="fsa-nav" aria-label={tr('t3d1944c7c7', "Sales approval sections")}>{tabs.map(({path,text,icon:Icon}) => <Link key={path} className={`fsa-tab ${path === (view === 'overview' ? tabs[0].path : view === 'subscriptions' ? tabs[1].path : tabs[2].path) ? 'active' : ''}`} to={path}><Icon size={17}/>{tr('tab_'+(path === tabs[0].path ? 'overview' : path === tabs[1].path ? 'subscriptions' : 'orders'), text)}</Link>)}</nav>

    {view === 'overview' && <section className="fsa-stats">

      {[[tr('ta28735af01', "Registrations"), summary.registrations, ClipboardCheck], [tr('te9e8ff782d', "Subscriptions awaiting review"), summary.subscriptions_pending, CreditCard], [tr('t2b4c517943', "QR orders awaiting review"), summary.qr_orders_pending, PackageCheck], [tr('ta5339cfeed', "Payments to verify"), (summary.subscription_payments_pending || 0) + (summary.qr_payments_pending || 0), ShieldCheck]].map(([name, count, Icon]) => <article key={name} className="fsa-stat"><Icon size={22}/><span>{name}</span><strong>{count ?? '—'}</strong></article>)}

    </section>}

    {loading ? <div className="fsa-empty">{tr('tfbf91f3634', "Loading verification requests…")}</div> : visible.length === 0 ? <div className="fsa-empty">{tr('td23558e1ab', "No requests in this section.")}</div> :

      <section className="fsa-list">{visible.map(item => <article className="fsa-card" key={item.id}>

        <div className="fsa-card-head"><div><small>{tr('t0f98b7f230', "Registration")} #{item.id} · {date(item.created_at)}</small><h2>{item.business_name}</h2><p>{item.contact_name || tr('tf5cb8fb0ba', "No contact")} · {item.phone} · {item.city || tr('t2814eaaff8', "City not specified")} · {tr('agent','المندوب')} #{item.agent_id}</p></div>

          <button type="button" className="fsa-details" onClick={() => setOpenId(item.id)}>{tr('ta082e30c2d', "View full details")}</button></div>

        {(view === 'overview' || view === 'subscriptions') && item.subscription_period !== 'none' && <div className="fsa-section">

          <div className="fsa-section-title"><CreditCard size={19}/><strong>{tr('t787ad0b7a1', "Subscription")} · {item.subscription_period}</strong><span className="fsa-pill">{item.subscription_status}</span><span className="fsa-payment">{tr('ta0e8e26f6f', "Payment:")} {item.payment_status}</span></div>

          <p className="fsa-money">{money(item.subscription_price_mad)}</p>

          {buttons(item, 'subscription')}

        </div>}

        {(view === 'overview' || view === 'orders') && item.lines?.length > 0 && <div className="fsa-section">

          <div className="fsa-section-title"><PackageCheck size={19}/><strong>{tr('t912833fb1d', "QR print request")}</strong><span className="fsa-pill">{item.qr_order_status}</span><span className="fsa-payment">{tr('ta0e8e26f6f', "Payment:")} {item.qr_payment_status}</span></div>

          <div className="fsa-totals"><span>{tr('t856ab10922', "Subtotal:")} {money(item.product_subtotal)}</span><span>{tr('te84bf52c80', "Promotion:")} −{money(item.promotion_discount)}</span><strong>{tr('t3cdee66b0b', "QR estimate:")} {money(item.product_total)}</strong></div>

          {item.missing_price_units > 0 && <p className="fsa-caution">{item.missing_price_units} {tr('unknownPrices','وحدات بلا سعر معروف. تحقق من المبلغ النهائي قبل تأكيد الدفع.')}</p>}

          {buttons(item, 'qr-order')}

        </div>}

      </article>)}</section>}

    {selected && createPortal(

      <div className="fsa-modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setOpenId(null); }}>

        <section className="fsa-modal" role="dialog" aria-modal="true" aria-labelledby="fsa-modal-title">

          <header className="fsa-modal-header">

            <div>

              <span className="fsa-kicker"><ShieldCheck size={16}/> {tr('registrationNo','التسجيل رقم')} #{selected.id}</span>

              <h2 id="fsa-modal-title">{selected.business_name}</h2>

              <p>{tr('t5c835c2242', "Submitted")} {date(selected.created_at)} · {tr('registration','التسجيل')}: {selected.status || '—'}</p>

            </div>

            <button type="button" className="fsa-modal-close" aria-label={tr('td1a5096b0b', "Close details")} onClick={() => setOpenId(null)}><X size={21}/></button>

          </header>

          <div className="fsa-modal-body">

            <section className="fsa-modal-section">

              <h3>{tr('tb13743c897', "Business & contact")}</h3>

              <dl className="fsa-modal-fields">

                <div><dt>{tr('td6e6cb19e3', "Business")}</dt><dd>{selected.business_name || '—'}</dd></div>

                <div><dt>{tr('tbbaff12800', "Contact")}</dt><dd>{selected.contact_name || '—'}</dd></div>

                <div><dt>{tr('tbcc254b55c', "Phone")}</dt><dd>{selected.phone || '—'}</dd></div>

                <div><dt>{tr('t57d056ed09', "City")}</dt><dd>{selected.city || '—'}</dd></div>

                <div><dt>{tr('t7dad0917d6', "Sales agent")}</dt><dd>#{selected.agent_id ?? '—'}</dd></div>

                <div><dt>{tr('t486302e5f6', "Manager user ID")}</dt><dd>{selected.manager_user_id ?? tr('t6ae2d6552c', "Not created")}</dd></div>

                <div><dt>{tr('t0022c29b17', "Establishment ID")}</dt><dd>{selected.establishment_id ?? tr('t6ae2d6552c', "Not created")}</dd></div>

              </dl>

              {selected.notes && <p className="fsa-modal-notes"><strong>{tr('t0f98b7f230', "Registration")} notes:</strong> {selected.notes}</p>}

            </section>

            <section className="fsa-modal-section">

              <h3><CreditCard size={18}/> {tr('t787ad0b7a1', "Subscription")}</h3>

              <dl className="fsa-modal-fields">

                <div><dt>{tr('t0b6cbdf7ad', "Plan")}</dt><dd>{selected.subscription_period || '—'}</dd></div>

                <div><dt>{tr('t3601146c4e', "Price")}</dt><dd>{money(selected.subscription_price_mad)}</dd></div>

                <div><dt>{tr('tec53a8c4f0', "Status")}</dt><dd>{selected.subscription_status || '—'}</dd></div>

                <div><dt>{tr('tc453a4b8e8', "Payment")}</dt><dd>{selected.payment_status || '—'}</dd></div>

                <div><dt>{tr('t161f25f902', "Starts")}</dt><dd>{date(selected.subscription_started_at)}</dd></div>

                <div><dt>{tr('tcfc36842a0', "Expires")}</dt><dd>{date(selected.subscription_expires_at)}</dd></div>

              </dl>

              {selected.subscription_review_note && <p className="fsa-modal-notes"><strong>{tr('t787ad0b7a1', "Subscription")} review:</strong> {selected.subscription_review_note}</p>}

            </section>

            <section className="fsa-modal-section">

              <h3><PackageCheck size={18}/> {tr('ta6efffbfeb', "QR print order")}</h3>

              <dl className="fsa-modal-fields">

                <div><dt>{tr('t57297718fd', "Order status")}</dt><dd>{selected.qr_order_status || '—'}</dd></div>

                <div><dt>{tr('tc453a4b8e8', "Payment")}</dt><dd>{selected.qr_payment_status || '—'}</dd></div>

                <div><dt>{tr('t2194aaec8c', "Subtotal")}</dt><dd>{money(selected.product_subtotal)}</dd></div>

                <div><dt>{tr('t679620f3bd', "Promotion discount")}</dt><dd>−{money(selected.promotion_discount)}</dd></div>

                <div><dt>{tr('tcd2109de90', "QR estimate")}</dt><dd>{money(selected.product_total)}</dd></div>

                <div><dt>{tr('t830d07f640', "Combined estimate")}</dt><dd><strong>{money(selected.combined_total)}</strong></dd></div>

              </dl>

              {selected.missing_price_units > 0 && <p className="fsa-caution">{selected.missing_price_units} {tr('unknownPrices','وحدات بلا سعر معروف. تحقق من المبلغ النهائي قبل تأكيد الدفع.')}</p>}

              {selected.lines?.length > 0 ? <div className="fsa-scroll"><table>

                <thead><tr><th>{tr('tefbfc3e625', "QR product")}</th><th>{tr('t03ab340b3f', "Qty")}</th><th>{tr('tb24ce0cd39', "Free")}</th><th>{tr('t19c562a36a', "Unit")}</th><th>{tr('td8a224f4c6', "Estimated paid")}</th></tr></thead>

                <tbody>{selected.lines.map((line, index) => <tr key={`${line.product_id}-${index}`}>

                  <td>{line.name}</td><td>{line.quantity}</td><td>{line.free_quantity}</td>

                  <td>{line.unit_price === null ? tr('tcf5e0fb3aa', "On request") : money(line.unit_price)}</td>

                  <td>{line.unit_price === null ? '—' : money((line.quantity - line.free_quantity) * line.unit_price)}</td>

                </tr>)}</tbody>

              </table></div> : <p>{tr('t8a7e6ae32f', "No QR products requested.")}</p>}

              {selected.qr_review_note && <p className="fsa-modal-notes"><strong>{tr('t10cf0eb198', "QR review:")}</strong> {selected.qr_review_note}</p>}

            </section>

          </div>

          <footer className="fsa-modal-footer"><button type="button" className="fsa-details" onClick={() => setOpenId(null)}>{tr('td3d2e61733', "Close")}</button></footer>

        </section>

      </div>, document.body

    )}

    <p className="fsa-disclaimer">{tr('t3bf948c117', "These product amounts are estimates based on current catalog prices. Payment verification is an explicit administrative confirmation, not an automatic payment gateway check.")}</p>

  </main>;

}
